import { useCallback, useEffect, useState } from 'react'

import { pushApi } from '@carrent/api'
import { push } from '@carrent/platform'
import { Icon, PageHeader } from '@carrent/ui'

/**
 * Настройка уведомлений.
 *
 * Честный экран: показывает фактическое состояние (разрешение +
 * подписка), включает/выключает, и — главное — объясняет, почему
 * пуши могут пропадать НЕ по вине приложения. Battery optimization
 * на Android убивает доставку независимо от качества кода; лучшее,
 * что может сделать приложение, — рассказать водителю, где у его
 * вендора выключается удушение фона. Стандартная практика
 * frontline-приложений.
 */

const VENDORS = [
  {
    id: 'xiaomi',
    name: 'Xiaomi / Redmi / POCO (MIUI, HyperOS)',
    steps:
      'Настройки → Приложения → CarRent → Экономия батареи → «Нет ограничений». '
      + 'Затем Настройки → Батарея → Автозапуск — включить для CarRent.',
  },
  {
    id: 'huawei',
    name: 'Huawei / Honor (EMUI)',
    steps:
      'Настройки → Батарея → Запуск приложений → CarRent → выключить '
      + '«Управлять автоматически», включить все три пункта вручную.',
  },
  {
    id: 'samsung',
    name: 'Samsung (One UI)',
    steps:
      'Настройки → Батарея → Ограничения в фоне → убрать CarRent из '
      + '«Приложения в спящем режиме» и «Глубокий сон».',
  },
  {
    id: 'other',
    name: 'Другие Android',
    steps:
      'Настройки → Приложения → CarRent → Батарея → «Без ограничений» '
      + '(название пункта зависит от прошивки).',
  },
]

export default function NotificationsPage() {
  const [status, setStatus] = useState('checking') // checking|unsupported|off|blocked|on
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [openVendor, setOpenVendor] = useState(null)

  const refresh = useCallback(async () => {
    if (!push.isSupported()) return setStatus('unsupported')
    const perm = push.permission()
    if (perm === 'denied') return setStatus('blocked')
    const sub = await push.getSubscription().catch(() => null)
    setStatus(perm === 'granted' && sub ? 'on' : 'off')
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const enable = async () => {
    setBusy(true)
    setError('')
    try {
      const perm = await push.requestPermission()
      if (perm !== 'granted') {
        setStatus(perm === 'denied' ? 'blocked' : 'off')
        return
      }
      const { data } = await pushApi.publicKey()
      const subscription = await push.subscribe(data.data.public_key)
      await pushApi.subscribe(subscription)
      setStatus('on')
    } catch (e) {
      setError(
        e?.response?.status === 503
          ? 'Пуши не настроены на сервере — сообщите администратору'
          : 'Не удалось включить уведомления'
      )
    } finally {
      setBusy(false)
    }
  }

  const disable = async () => {
    setBusy(true)
    setError('')
    try {
      const endpoint = await push.unsubscribe()
      if (endpoint) await pushApi.unsubscribe(endpoint)
      setStatus('off')
    } catch {
      setError('Не удалось выключить')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="p-page flex-col gap">
      <PageHeader title="Уведомления" />

      <div className="card notif__status">
        {status === 'checking' && <span className="spinner" aria-label="Проверка" />}

        {status === 'unsupported' && (
          <p className="text-muted">
            Этот браузер не поддерживает уведомления. На iPhone добавьте
            приложение на экран «Домой» (Поделиться → На экран «Домой») —
            уведомления заработают из установленной версии.
          </p>
        )}

        {status === 'blocked' && (
          <p className="text-muted">
            Уведомления запрещены в настройках браузера. Разрешите их
            для этого сайта и вернитесь сюда.
          </p>
        )}

        {status === 'off' && (
          <>
            <p>
              Сообщения диспетчера будут приходить, даже когда приложение
              закрыто.
            </p>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={enable}
            >
              Включить уведомления
            </button>
          </>
        )}

        {status === 'on' && (
          <>
            <p className="notif__on">
              <Icon name="check" size={18} /> Уведомления включены
              на этом устройстве
            </p>
            <button
              type="button"
              className="btn btn--secondary"
              disabled={busy}
              onClick={disable}
            >
              Выключить
            </button>
          </>
        )}

        {error && <p className="notif__error">{error}</p>}
      </div>

      <div className="card">
        <h2 className="notif__vendors-title">Пуши приходят с задержкой?</h2>
        <p className="text-muted notif__vendors-hint">
          На многих Android экономия батареи усыпляет приложения в фоне —
          уведомления копятся и приходят пачкой. Это настройка телефона,
          не приложения. Найдите свой:
        </p>
        {VENDORS.map((v) => (
          <div key={v.id} className="notif__vendor">
            <button
              type="button"
              className="notif__vendor-toggle"
              aria-expanded={openVendor === v.id}
              onClick={() => setOpenVendor(openVendor === v.id ? null : v.id)}
            >
              <span>{v.name}</span>
              <Icon
                name={openVendor === v.id ? 'chevronLeft' : 'chevronRight'}
                size={16}
              />
            </button>
            {openVendor === v.id && (
              <p className="notif__vendor-steps text-muted">{v.steps}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
